import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
    Minus,
    Plus,
    ChevronRight,
    Trash2,
    X,
    Camera,
    Loader2,
    Search,
    Tag,
} from "lucide-react";
import { api } from "@/lib/api";
import { Money, ListShell } from "@/components/ui-veda/Primitives";
import { TID } from "@/constants/testIds";

const FALLBACK_CATEGORIES = [
    "Immunity",
    "Digestion",
    "Stress Relief",
    "Skin Care",
    "Hair Care",
    "Women Wellness",
    "Body Care",
];

function EditSheet({ initial, existingCategories, onClose, onSaved }) {
    const isNew = !initial?.product_id;
    const fileRef = useRef(null);
    const [f, setF] = useState({
        name: initial?.name || "",
        category: initial?.category || "",
        company: initial?.company || "",
        price: initial?.price ?? 0,
        stock: initial?.stock ?? 0,
        description: initial?.description || "",
        ingredients: initial?.ingredients || "",
        benefits: initial?.benefits || "",
        dosage: initial?.dosage || "",
        image_url: initial?.image_url || "",
    });
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);

    function set(k, v) {
        setF((prev) => ({ ...prev, [k]: v }));
    }
    function pickImage(e) {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 3 * 1024 * 1024) {
            toast.error("Image too large. Under 3 MB please.");
            return;
        }
        const reader = new FileReader();
        reader.onload = () => set("image_url", reader.result);
        reader.readAsDataURL(file);
    }
    async function save() {
        setSaving(true);
        try {
            const body = {
                ...f,
                name: f.name.trim(),
                category: (f.category || "").trim(),
                company: (f.company || "").trim() || null,
                price: Number(f.price),
                stock: Number(f.stock),
            };
            if (!body.name) {
                toast.error("Name is required.");
                return;
            }
            if (!body.category) {
                toast.error("Category is required.");
                return;
            }
            if (isNew) {
                await api.post("/products", body);
                toast.success("Product created.");
            } else {
                await api.put(`/products/${initial.product_id}`, body);
                toast.success("Saved.");
            }
            onSaved();
            onClose();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not save.");
        } finally {
            setSaving(false);
        }
    }
    async function remove() {
        if (!window.confirm(`Delete "${f.name}"?`)) return;
        setDeleting(true);
        try {
            await api.delete(`/products/${initial.product_id}`);
            toast.success("Deleted.");
            onSaved();
            onClose();
        } catch {
            toast.error("Could not delete.");
        } finally {
            setDeleting(false);
        }
    }

    return (
        <div
            className="fixed inset-0 z-[60] flex items-end justify-center bg-veda-inverse/40 backdrop-blur-sm animate-fade-in sm:items-center"
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div className="max-h-[90vh] w-full max-w-md overflow-y-auto border-t border-veda-border bg-veda-surface p-5 sm:border">
                <div className="flex items-center justify-between">
                    <p className="font-display text-2xl text-veda-onSurface">
                        {isNew ? "New product" : "Edit product"}
                    </p>
                    <button onClick={onClose} className="p-1 text-veda-muted">
                        <X size={18} />
                    </button>
                </div>

                <div className="mt-4 border border-veda-border bg-veda-surface2 p-3">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-muted">
                        Product photo
                    </p>
                    <div className="mt-2 flex items-center gap-3">
                        <div className="h-20 w-20 shrink-0 overflow-hidden border border-veda-border bg-veda-surface">
                            {f.image_url ? (
                                <img
                                    src={f.image_url}
                                    alt=""
                                    className="h-full w-full object-cover"
                                    data-testid={TID.editProductPreview}
                                />
                            ) : (
                                <div className="grid h-full w-full place-items-center text-veda-muted">
                                    <Camera size={16} />
                                </div>
                            )}
                        </div>
                        <div className="flex flex-col gap-2">
                            <button
                                type="button"
                                onClick={() => fileRef.current?.click()}
                                data-testid={TID.editProductUploadBtn}
                                className="inline-flex items-center gap-1.5 border border-veda-border bg-veda-surface px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.14em]"
                            >
                                <Camera size={12} />
                                {f.image_url ? "Change photo" : "Upload photo"}
                            </button>
                            {f.image_url && (
                                <button
                                    type="button"
                                    onClick={() => set("image_url", "")}
                                    data-testid={TID.editProductRemoveImage}
                                    className="text-left text-[10px] font-semibold uppercase tracking-[0.14em] text-veda-error underline underline-offset-2"
                                >
                                    Remove
                                </button>
                            )}
                            <input
                                ref={fileRef}
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={pickImage}
                            />
                        </div>
                    </div>
                    <div className="mt-3">
                        <label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-veda-muted">
                            …or paste an image URL
                        </label>
                        <input
                            type="url"
                            inputMode="url"
                            placeholder="https://example.com/product.jpg"
                            value={f.image_url?.startsWith("data:") ? "" : f.image_url}
                            onChange={(e) => set("image_url", e.target.value)}
                            data-testid={TID.editProductUrlInput}
                            className="mt-1 w-full border border-veda-border bg-veda-surface px-3 py-2 text-xs focus:border-veda-brand focus:outline-none"
                        />
                        <p className="mt-1.5 text-[10px] text-veda-muted">
                            JPG/PNG/WebP. Upload keeps things simple; a hosted
                            URL loads faster on the storefront.
                        </p>
                    </div>
                </div>

                <div className="mt-4 flex flex-col gap-3">
                    <input
                        placeholder="Name"
                        value={f.name}
                        onChange={(e) => set("name", e.target.value)}
                        data-testid={TID.editProductName}
                        className="border border-veda-border bg-veda-surface px-3 py-2.5 text-sm"
                    />
                    <input
                        placeholder="Company / Brand (e.g. Kerala Ayurveda)"
                        value={f.company}
                        onChange={(e) => set("company", e.target.value)}
                        data-testid={TID.editProductCompany}
                        className="border border-veda-border bg-veda-surface px-3 py-2.5 text-sm"
                    />

                    <div>
                        <label className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-veda-muted">
                            <Tag size={11} /> Category
                        </label>
                        <input
                            placeholder="Type a category (e.g. Immunity, Panchakarma, …)"
                            value={f.category}
                            onChange={(e) => set("category", e.target.value)}
                            list="veda-admin-categories"
                            data-testid={TID.editProductCategory}
                            className="mt-1 w-full border border-veda-border bg-veda-surface px-3 py-2.5 text-sm"
                        />
                        <datalist id="veda-admin-categories">
                            {existingCategories.map((c) => (
                                <option key={c} value={c} />
                            ))}
                        </datalist>
                        <div className="mt-2 flex flex-wrap gap-1.5">
                            {existingCategories.map((c) => (
                                <button
                                    key={c}
                                    type="button"
                                    onClick={() => set("category", c)}
                                    data-testid={TID.editProductCategoryChip(
                                        c.replace(/\s+/g, "-").toLowerCase(),
                                    )}
                                    className={`rounded-pill border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] ${
                                        f.category === c
                                            ? "border-veda-inverse bg-veda-inverse text-veda-onInverse"
                                            : "border-veda-border bg-veda-surface text-veda-onSurface hover:bg-veda-surface2"
                                    }`}
                                >
                                    {c}
                                </button>
                            ))}
                        </div>
                        <p className="mt-1.5 text-[10px] text-veda-muted">
                            Pick an existing tag above or type a brand-new
                            category — it will be saved and reused.
                        </p>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <input
                            type="number"
                            step="0.01"
                            placeholder="Price"
                            value={f.price}
                            onChange={(e) => set("price", e.target.value)}
                            className="border border-veda-border bg-veda-surface px-3 py-2.5 text-sm"
                        />
                        <input
                            type="number"
                            placeholder="Stock"
                            value={f.stock}
                            onChange={(e) => set("stock", e.target.value)}
                            className="border border-veda-border bg-veda-surface px-3 py-2.5 text-sm"
                        />
                    </div>
                    <textarea
                        rows={3}
                        placeholder="Description"
                        value={f.description}
                        onChange={(e) => set("description", e.target.value)}
                        className="border border-veda-border bg-veda-surface px-3 py-2.5 text-sm"
                    />
                    <textarea
                        rows={2}
                        placeholder="Ingredients"
                        value={f.ingredients}
                        onChange={(e) => set("ingredients", e.target.value)}
                        className="border border-veda-border bg-veda-surface px-3 py-2.5 text-sm"
                    />
                    <textarea
                        rows={2}
                        placeholder="Benefits"
                        value={f.benefits}
                        onChange={(e) => set("benefits", e.target.value)}
                        className="border border-veda-border bg-veda-surface px-3 py-2.5 text-sm"
                    />
                    <textarea
                        rows={2}
                        placeholder="Dosage"
                        value={f.dosage}
                        onChange={(e) => set("dosage", e.target.value)}
                        className="border border-veda-border bg-veda-surface px-3 py-2.5 text-sm"
                    />
                </div>

                <div className="mt-5 flex items-center gap-2">
                    <button
                        data-testid={TID.saveProduct}
                        onClick={save}
                        disabled={saving}
                        className="inline-flex flex-1 items-center justify-center gap-2 bg-veda-brand px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.16em] text-white disabled:opacity-70"
                    >
                        {saving && <Loader2 size={13} className="animate-spin" />}
                        Save
                    </button>
                    {!isNew && (
                        <button
                            onClick={remove}
                            disabled={deleting}
                            className="inline-flex items-center gap-1 border border-veda-error px-3 py-2.5 text-xs font-semibold uppercase tracking-[0.16em] text-veda-error"
                        >
                            <Trash2 size={13} /> Delete
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function AdminProducts() {
    const [items, setItems] = useState([]);
    const [categories, setCategories] = useState(FALLBACK_CATEGORIES);
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // null | 'new' | product

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/products", {
                params: q.trim() ? { q: q.trim() } : {},
            });
            setItems(data);
        } finally {
            setLoading(false);
        }
    }, [q]);

    const loadCategories = useCallback(async () => {
        try {
            const { data } = await api.get("/product-categories");
            const merged = Array.from(
                new Set([...(data || []), ...FALLBACK_CATEGORIES]),
            ).sort();
            setCategories(merged);
        } catch {
            /* keep fallback */
        }
    }, []);

    useEffect(() => {
        const t = setTimeout(load, 200);
        return () => clearTimeout(t);
    }, [load]);

    useEffect(() => {
        loadCategories();
    }, [loadCategories]);

    // Optimistic inline PUT
    async function bumpField(p, field, delta) {
        const orig = p[field];
        const next = Math.max(0, Number(orig) + delta);
        setItems((prev) =>
            prev.map((x) =>
                x.product_id === p.product_id ? { ...x, [field]: next } : x,
            ),
        );
        try {
            await api.put(`/products/${p.product_id}`, { [field]: next });
        } catch {
            toast.error("Update failed.");
            setItems((prev) =>
                prev.map((x) =>
                    x.product_id === p.product_id ? { ...x, [field]: orig } : x,
                ),
            );
        }
    }

    async function changeRowImage(p, file) {
        if (!file) return;
        if (file.size > 3 * 1024 * 1024) {
            toast.error("Image too large. Under 3 MB please.");
            return;
        }
        const dataUrl = await new Promise((res, rej) => {
            const r = new FileReader();
            r.onload = () => res(r.result);
            r.onerror = rej;
            r.readAsDataURL(file);
        });
        const orig = p.image_url;
        setItems((prev) =>
            prev.map((x) =>
                x.product_id === p.product_id ? { ...x, image_url: dataUrl } : x,
            ),
        );
        try {
            await api.put(`/products/${p.product_id}`, { image_url: dataUrl });
            toast.success("Photo updated.");
        } catch {
            toast.error("Could not update photo.");
            setItems((prev) =>
                prev.map((x) =>
                    x.product_id === p.product_id ? { ...x, image_url: orig } : x,
                ),
            );
        }
    }

    let listBody;
    if (loading || items.length === 0) {
        listBody = (
            <ListShell
                loading={loading}
                isEmpty={!loading && items.length === 0}
                emptyMessage="No products found."
            />
        );
    } else {
        listBody = (
            <ul className="divide-y divide-veda-divider">
                {items.map((p) => (
                    <li
                        key={p.product_id}
                        className="grid grid-cols-[64px_1fr_auto] items-center gap-3 py-3"
                    >
                        <RowImageSwap product={p} onFile={(file) => changeRowImage(p, file)} />
                        <div className="min-w-0">
                            <p className="line-clamp-1 text-sm font-semibold text-veda-onSurface">
                                {p.name}
                            </p>
                            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-veda-muted">
                                {p.company ? `${p.company} · ` : ""}
                                {p.category}
                            </p>
                            <div className="mt-2 flex flex-wrap gap-2">
                                <Stepper
                                    label="₹"
                                    value={p.price}
                                    onDec={() => bumpField(p, "price", -10)}
                                    onInc={() => bumpField(p, "price", 10)}
                                />
                                <Stepper
                                    label="Stock"
                                    value={p.stock}
                                    onDec={() => bumpField(p, "stock", -1)}
                                    onInc={() => bumpField(p, "stock", 1)}
                                    warn={p.stock < 5}
                                />
                            </div>
                        </div>
                        <button
                            data-testid={TID.editProduct(p.product_id)}
                            onClick={() => setEditing(p)}
                            className="grid h-8 w-8 place-items-center border border-veda-border text-veda-onSurface"
                            aria-label="Edit"
                        >
                            <ChevronRight size={15} />
                        </button>
                    </li>
                ))}
            </ul>
        );
    }

    return (
        <div data-testid={TID.adminProducts} className="flex flex-col gap-4">
            <div className="flex items-center gap-2">
                <div className="flex flex-1 items-center border border-veda-border bg-veda-surface">
                    <Search size={14} className="ml-3 text-veda-muted" />
                    <input
                        type="search"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search products"
                        className="flex-1 bg-transparent px-2 py-2.5 text-sm focus:outline-none"
                    />
                </div>
                <button
                    onClick={() => setEditing("new")}
                    className="inline-flex items-center gap-1 bg-veda-inverse px-3 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-veda-onInverse"
                >
                    <Plus size={13} /> New
                </button>
            </div>

            {listBody}

            {editing && (
                <EditSheet
                    initial={editing === "new" ? null : editing}
                    existingCategories={categories}
                    onClose={() => setEditing(null)}
                    onSaved={() => {
                        load();
                        loadCategories();
                    }}
                />
            )}
        </div>
    );
}

function Stepper({ label, value, onDec, onInc, warn }) {
    return (
        <div className="inline-flex items-center gap-1 border border-veda-border bg-veda-surface text-xs">
            <span
                className={`px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] ${
                    warn ? "text-veda-warning" : "text-veda-muted"
                }`}
            >
                {label}
            </span>
            <button
                onClick={onDec}
                className="grid h-7 w-7 place-items-center border-l border-veda-border"
                aria-label="Decrease"
            >
                <Minus size={11} />
            </button>
            <span
                className={`min-w-[42px] px-2 text-center font-semibold tabular-nums ${
                    warn ? "text-veda-warning" : "text-veda-onSurface"
                }`}
            >
                {label === "₹"
                    ? Number(value || 0).toLocaleString("en-IN")
                    : value}
            </span>
            <button
                onClick={onInc}
                className="grid h-7 w-7 place-items-center border-l border-veda-border"
                aria-label="Increase"
            >
                <Plus size={11} />
            </button>
        </div>
    );
}

function RowImageSwap({ product, onFile }) {
    const fileRef = useRef(null);
    const hasImg = Boolean(product.image_url);
    return (
        <div className="group relative h-16 w-16">
            {hasImg ? (
                <img
                    src={product.image_url}
                    alt=""
                    className="h-full w-full object-cover"
                />
            ) : (
                <div className="grid h-full w-full place-items-center border border-dashed border-veda-border bg-veda-surface2 text-veda-muted">
                    <Camera size={14} />
                </div>
            )}
            <button
                type="button"
                onClick={() => fileRef.current?.click()}
                data-testid={TID.rowImageSwapBtn(product.product_id)}
                aria-label={hasImg ? "Change photo" : "Add photo"}
                className="absolute inset-0 flex items-end justify-center bg-veda-inverse/0 transition-colors hover:bg-veda-inverse/50"
            >
                <span className="mb-1 inline-flex items-center gap-1 rounded-pill bg-veda-inverse/90 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.14em] text-veda-onInverse opacity-0 transition-opacity group-hover:opacity-100">
                    <Camera size={9} /> {hasImg ? "Change" : "Add"}
                </span>
            </button>
            <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = "";
                    onFile(f);
                }}
            />
        </div>
    );
}